import React from 'react';

import clsx from 'clsx';
import { ColorTemplateIcon } from 'src/components/icons/ColorTemplateIcon';
import { ContractIcon } from 'src/components/icons/ContractIcon';
import type { SimpleIconInterface } from 'src/components/icons/icon-utils';
import { ImageIcon } from 'src/components/icons/ImageIcon';
import { MSExcelIcon } from 'src/components/icons/MSExcelIcon';
import { MSPPTIcon } from 'src/components/icons/MSPPTIcon';
import { MSWordIcon } from 'src/components/icons/MSWordIcon';
import { PDFIcon } from 'src/components/icons/PDFIcon';
import { TextDocumentIcon } from 'src/components/icons/TextDocumentIcon';
import { Apollo } from 'src/gql/generated';
import type { FileLocationDocument } from 'src/helpers';
import {
  excelMimeTypes,
  getFileLocationFromDocument,
  pdfMimeTypes,
  pptMimeTypes,
  wordMimeTypes,
} from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

type FileFormat = 'pdf' | 'msWord' | 'msPPT' | 'msExcel' | 'image' | 'default';

export function getFileFormat(mimetype: string): FileFormat {
  if (pdfMimeTypes.some(m => m === mimetype)) return 'pdf';
  if (wordMimeTypes.some(m => m === mimetype)) return 'msWord';
  if (pptMimeTypes.some(m => m === mimetype)) return 'msPPT';
  if (excelMimeTypes.some(m => m === mimetype)) return 'msExcel';
  if (mimetype.startsWith('image/')) return 'image';

  return 'default';
}

export type StorableIconDocument = FileLocationDocument & Pick<Apollo.Document, 'variant'>;

interface StorableIconProps {
  storable: StorableIconDocument;
  className?: string;
  iconProps?: SimpleIconInterface;
}

function GetIcon({ iconProps, storable }: StorableIconProps): JSX.Element {
  const mimetype = getFileLocationFromDocument(storable)?.fileLocation.mimetype;
  if (mimetype) return <FileTypeIcon iconProps={iconProps} mimetype={mimetype} />;
  if (storable.variant === Apollo.DocumentVariant.Template)
    return <ColorTemplateIcon {...iconProps} />;
  return <ContractIcon {...iconProps} />;
}

export function FileTypeIcon({
  iconProps,
  mimetype,
}: {
  mimetype: string;
  iconProps?: SimpleIconInterface;
}): JSX.Element {
  const fileFormat = getFileFormat(mimetype);

  switch (fileFormat) {
    case 'pdf':
      return <PDFIcon {...iconProps} />;
    case 'msWord':
      return <MSWordIcon {...iconProps} />;
    case 'msPPT':
      return <MSPPTIcon {...iconProps} />;
    case 'msExcel':
      return <MSExcelIcon {...iconProps} />;
    case 'image':
      return <ImageIcon {...iconProps} />;
    case 'default':
      return <TextDocumentIcon {...iconProps} />;
  }
}

const useStyles = makeStyles<
  Pick<NonNullable<StorableIconProps['iconProps']>, 'height' | 'width'>
>()((_theme, { height, width }) => ({
  // Prevents weird re-sizing of the SVG in places like the file explorer (e.g. when storable name is very long in a table cell).
  storableIconWrapper: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    height,
    justifyContent: 'center',
    width,
  },
}));

export function StorableIcon(props: StorableIconProps): JSX.Element {
  const { className, iconProps = {} } = props;
  const { classes } = useStyles({ ...iconProps });

  return (
    <span className={clsx(classes.storableIconWrapper, className)}>
      <GetIcon {...props} />
    </span>
  );
}
